import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { useEffect, useState } from "react";
import { Navbar } from "../../components/Navbar";
import env from "../../config/env";
import { useUserInfo } from "../../context/user";
import AgentCard from "./components/AgentCard";
import FilterNav from "./components/FilterNav";
import SideBar from "./components/SideBar";
import { useAppServices } from "../../hook/services";
import IntegrationModal from "./components/IntegrationModal";
import { useUsersContext } from "../../context/usersContext";
import useDebouncedEffect from "../../hook/debounce";

const YourAIAgents = () => {
  const userActiveLocation = useUserInfo()[4];
  const BASE_URL = env.API_URL + "/v1";
  const userInfo = useUserInfo()[0];
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [creatorFilter, setCreatorFilter] = useState("");
  const [pluginsData, setPluginsData] = useState(
    userActiveLocation?.agenct_config?.plugins.filter(
      (data) => data?.is_active_for_account
    ) || []
  );
  const Service = useAppServices();
  const [pluginFilter, setPluginFilter] = useState("");
  const [activeOnly, setActiveOnly] = useState(true); // default: true
  const [selectTags, setSelectTags] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const { agents, fetchAgentsArray, agentPageLoading } = useUsersContext();
  const {
    SSO,
    showToast,
    companyInfo,
    getLocationUsers,
    locationUsers,
    setOnFromPrompt,
    ChatSubmit,
    setUserAsUnread,
    addNewMessage,
    refresh,
  } = useUsersContext();

  // const loadPluginData = async () => {
  //   const { response } = await Service.assistant.GetFunctions();

  //   if (response) {
  //     setPluginsData(response?.data?.data);
  //   }
  // };

  // useEffect(() => {
  //   loadPluginData();
  // }, []);

  const handleRefresh = () => {
    fetchAgentsArray();
  };

  useDebouncedEffect(
    () => {
      handleRefresh();
    },
    [userActiveLocation],
    300
  );

  const getFilteredAgents = () => {
    let filtered = [...agents];

    // Filter by search
    if (searchQuery.trim() !== "") {
      filtered = filtered.filter(
        (agent) =>
          agent.assistant_name
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (agent.function_names &&
            agent.function_names.some(
              (fn) =>
                typeof fn === "string" &&
                fn.toLowerCase().includes(searchQuery.toLowerCase())
            ))
      );
    }

    // Filter by Creator (assist_type)
    if (creatorFilter) {
      filtered = filtered.filter(
        (agent) => agent.assist_type === creatorFilter
      );
    }

    // Sort
    if (sortOrder === "asc") {
      filtered.sort((a, b) => a.assistant_name.localeCompare(b.assistant_name));
    } else if (sortOrder === "desc") {
      filtered.sort((a, b) => b.assistant_name.localeCompare(a.assistant_name));
    } else if (sortOrder === "createdAt") {
      filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortOrder === "updatedAt") {
      filtered.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    } else {
      filtered.sort((a, b) => {
        if (a.pin === b.pin) return 0;
        return a.pin ? -1 : 1;
      });
    }

    if (pluginFilter) {
      const plugins = pluginsData.filter((item) =>
        pluginFilter.some((plugin) =>
          item?.name?.toLowerCase()?.includes(plugin.toLowerCase())
        )
      );

      filtered = filtered.filter((agent) =>
        plugins.some((plugin) => agent.function_names?.includes(plugin?.name))
      );
    }

    if (activeOnly) {
      filtered = filtered.filter((agent) => agent.status === true);
    } else {
      filtered = filtered.filter((agent) => agent.status === false);
    }

    if (selectTags.length > 0) {
      filtered = filtered.filter((agent) => {
        const agentTags = agent?.subCategory || [];
        // Extract IDs from subCategory objects
        const agentTagIds = agentTags.map((tagObj) => String(tagObj.id));
        return selectTags.some((tag) => agentTagIds.includes(String(tag)));
      });
    }

    if (categoryFilter) {
      filtered = filtered.filter((agent) =>
        categoryFilter === "Your AI Agents"
          ? agent?.category?.length === 0
          : agent?.category?.[0]?.id === categoryFilter
      );
    }

    return filtered;
  };

  const filteredAgents = getFilteredAgents();

  return (
    <div className="h-screen flex flex-col">
      <Navbar />

      {agentPageLoading ? (
        <div className="w-full h-full flex justify-center items-center"><CircularProgress style={{ display: "block", margin: "20px auto" }} /></div>
      ) : (
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <div className="w-[230px] h-full sticky top-[80px] bg-white border-r border-[#D9D9D9] overflow-y-auto">
            <SideBar
              filteredAgents={filteredAgents}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              handleRefresh={handleRefresh}
              numberOfAgents={agents.length}
              companyInfo={companyInfo}
              setCategoryFilter={setCategoryFilter}
              categoryFilter={categoryFilter}
              setActiveOnly={setActiveOnly}
              activeOnly={activeOnly}
            />
          </div>

          {/* Main content */}
          <div className="flex-1 px-[58px] pt-[28px] overflow-y-auto bg-white">
            {searchQuery && (
              <h2 className="text-xl font-medium mb-4">
                {filteredAgents.length} Search Result
                {filteredAgents.length !== 1 && "s"} for :{" "}
                <span className="font-normal">{searchQuery}</span>
                {filteredAgents.length === 0 && (
                  <hr className="mt-6 w-[60%]" />
                )}
              </h2>
            )}

            {filteredAgents.length === 0 && searchQuery !== "" ? (
              <p className="font-inter mt-9">
                It doesn’t look like we have any Agents that match your search
                term.
              </p>
            ) : (
              <FilterNav
                setSelectTags={setSelectTags}
                selectTags={selectTags}
                companyInfo={companyInfo}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                creatorFilter={creatorFilter}
                setCreatorFilter={setCreatorFilter}
                agents={agents}
                pluginsData={pluginsData}
                pluginFilter={pluginFilter}
                setPluginFilter={setPluginFilter}
                activeOnly={activeOnly}
                setActiveOnly={setActiveOnly}
              />
            )}

            {filteredAgents.length === 0 &&
              !pluginFilter &&
              !creatorFilter &&
              agents.length > 0 &&
              searchQuery === "" && (
                <p className="font-inter mt-9">
                  It doesn’t look like we have any Agents that match your
                  search term.
                </p>
              )}
            <div
              style={{ zIndex: -10 }}
              className="pt-[31px] gap-[37px] flex flex-wrap"
            >
              {filteredAgents.map((agent, index) => (
                <AgentCard
                  key={agent.id || index}
                  data={agent}
                  handleRefresh={handleRefresh}
                  companyInfo={companyInfo}
                />
              ))}
            </div>
          </div>
        </div>)}
    </div>
  );
};

export default YourAIAgents;
